import React from 'react'
import { StacktraceFrame } from '../../domain/Error'
import Shorten from '../../helper/shorten'
import { BoxHeader, BoxTitle } from '../../ui/box'
import { IdeIntegrationButton } from '../IdeIntegrationButton'

interface Props {
    trace: StacktraceFrame[]
    errorType: string
    errorMessage: string
    packageVersions?: { [key: string]: string }
}

interface State {
    copied: boolean
}

export interface TraceLine {
    previousException?: { type: string; message: string; code: number }
    file?: string
    relativeFilename?: string
    function: string
    line?: number
    class?: string
    type?: string
    code?: string
    codeStartLine?: number
    version?: string
    package?: string
}

class StackTrace extends React.Component<Props, State> {
    public state: State = {
        copied: false,
    }
    public copyToClipboard = () => {
        if (this.clipboardApiAvailable()) {
            let text = this.props.errorType + ': ' + this.props.errorMessage + '\n'
            this.generateTraceLines()?.forEach(exception =>
                exception.forEach((traceLine, i) => {
                    const { file, line } = traceLine

                    if (traceLine.previousException) {
                        text += traceLine.previousException.type + ': ' + traceLine.previousException.message + '\n'
                    }
                    text += i + 1 + '. ' + traceLine.function + (file ? ' (' + file + ':' + line + ')\n' : '\n')
                }),
            )
            navigator.clipboard.writeText(text)
            this.setState({ copied: true })
        }
    }

    public render() {
        const traces = this.generateTraceLines()

        return traces.map((trace, index) => {
            return (
                <div key={index}>
                    {trace[0].previousException ? (
                        <BoxHeader>
                            <BoxTitle>
                                {trace[0].previousException.type}
                                {trace[0].previousException.code ? ' (' + trace[0].previousException.code + ')' : null}
                                <br />
                                <small>{trace[0].previousException.message}</small>
                            </BoxTitle>
                        </BoxHeader>
                    ) : null}
                    <ol className="stackTrace">
                        {trace.map((traceLine, i) => {
                            return (
                                <li className="stackTrace-row" key={i}>
                                    <div className="stackTrace-wrapper">
                                        {traceLine.function && (
                                            <div className="stackTrace-header">
                                                {traceLine.class && (
                                                    <span className="stackTrace-class">
                                                        <strong>
                                                            <Shorten string={traceLine.class} />
                                                        </strong>
                                                        {traceLine.type}
                                                    </span>
                                                )}
                                                <strong>
                                                    {traceLine.function}
                                                    {traceLine.function != '...' ? '()' : ''}
                                                </strong>
                                                <IdeIntegrationButton
                                                    pullRight={false}
                                                    traceLine={traceLine}
                                                    className="stackTrace-buttons"
                                                />
                                            </div>
                                        )}

                                        {traceLine.file &&
                                            (traceLine.code ? (
                                                <>
                                                    <details className="disclosureWidget">
                                                        <summary
                                                            className="disclosureWidget-toggle"
                                                            role="term"
                                                            aria-details={`${index}_${i}`}
                                                        >
                                                            <small>
                                                                {traceLine.file}:{traceLine.line}
                                                            </small>
                                                        </summary>
                                                    </details>
                                                    <div role="definition" className="disclosureWidget-content" id={`${index}_${i}`}>
                                                        <pre
                                                            className="stackTrace-code"
                                                            style={{
                                                                counterReset: 'line ' + traceLine.codeStartLine,
                                                            }}
                                                        >
                                                            {traceLine.code}
                                                        </pre>
                                                    </div>
                                                </>
                                            ) : (
                                                <span>
                                                    <small>
                                                        {traceLine.file}:{traceLine.line}
                                                    </small>
                                                </span>
                                            ))}
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                    {trace && trace.length > 0 && index === traces.length - 1 ? (
                        <div className="box-footer">
                            <div className="btn-group">
                                <a className="btn btn-default" onClick={this.copyToClipboard}>
                                    {this.state.copied ? 'Copied as Text' : 'Copy as Text'}
                                </a>
                            </div>
                        </div>
                    ) : null}
                </div>
            )
        })
    }

    private generateTraceLines = (): TraceLine[][] => {
        const surroundingLines = 5 // 5 before, 5 after, 11 in total

        if (!Array.isArray(this.props.trace)) {
            return []
        }

        const traces: TraceLine[][] = []

        const exceptions: StacktraceFrame[][] = []
        this.props.trace.forEach((frame, index) => {
            const position = exceptions.length - 1
            if (frame.previousException || index === 0) {
                exceptions.push([frame])
            } else {
                exceptions[position].push(frame)
            }
        })

        exceptions.forEach(exception => {
            const trace = exception.map(tl => {
                const traceLine: TraceLine = { ...tl }
                if (traceLine.line && traceLine.code) {
                    const lines = Array.isArray(traceLine.code) ? traceLine.code : traceLine.code.split(/\r?\n/)

                    let highlightedLine = Math.floor((lines.length - 1) / 2)
                    // Beginning or end of file
                    if (lines.length < surroundingLines * 2 + 1) {
                        // Beginning of file
                        if (traceLine.line < surroundingLines + 1) {
                            highlightedLine = lines.length - surroundingLines - 1
                        } else {
                            highlightedLine = lines.length - (lines.length - surroundingLines)

                            // It could happen that the error line is detected one line
                            // after the last line of the file by PHP
                            if (lines.length === surroundingLines) {
                                highlightedLine -= 1
                            }
                        }
                    }

                    const code: any = []
                    for (let j = 0; j < lines.length; j++) {
                        if (j === highlightedLine) {
                            code.push(
                                <span key={j} className="stackTrace-primaryLine">
                                    {lines[j]}
                                </span>,
                            )
                        } else {
                            code.push(<span key={j}>{lines[j]}</span>)
                        }
                    }

                    traceLine.code = code
                    // Counter-increment starts from one number higher
                    traceLine.codeStartLine = traceLine.line - highlightedLine - 1

                    return traceLine
                }

                return traceLine
            })

            traces.push(trace)
        })

        return traces
    }

    private clipboardApiAvailable = () => {
        return navigator.clipboard && typeof navigator.clipboard.writeText !== 'undefined'
    }
}

export default StackTrace
